import { useState, useEffect } from "react";
import FontPicker from "react-fontpicker-ts";
import "react-fontpicker-ts/dist/index.css";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

interface ColorsData {
  primary?: string;
  secondary?: string;
  accent?: string;
  background?: string;
}

interface TypographyData {
  headingFont?: string;
  headingColor?: string;
  bodyFont?: string;
  bodyColor?: string;
}

interface WebsiteDesignSettingsData {
  colors: ColorsData;
  typography: TypographyData;
}

function ColorField({
  id,
  label,
  value,
  defaultHex,
  onChange,
}: {
  id: string;
  label: string;
  value?: string;
  defaultHex: string;
  onChange: (value: string | undefined) => void;
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <div className="flex gap-2 items-center">
        <Input
          id={id}
          type="color"
          className="w-12 h-10 p-1 cursor-pointer"
          value={value || defaultHex}
          onChange={(e) => onChange(e.target.value)}
        />
        <Input
          type="text"
          placeholder={defaultHex}
          value={value || ""}
          onChange={(e) => onChange(e.target.value || undefined)}
        />
      </div>
    </div>
  );
}

export function SettingsWebsiteDesign() {
  const [settings, setSettings] = useState<WebsiteDesignSettingsData>({
    colors: {},
    typography: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);
      try {
        const res = await fetch("/api/website-design-settings");
        if (res.ok) {
          const data = await res.json();
          if (data.websiteDesignSettings) {
            setSettings({
              colors: data.websiteDesignSettings.colors || {},
              typography: data.websiteDesignSettings.typography || {},
            });
          }
        }
      } catch (error) {
        console.error("Failed to load website design settings:", error);
      } finally {
        setIsLoading(false);
      }
    }
    fetchData();
  }, []);

  const handleSave = async () => {
    setIsSaving(true);
    try {
      const res = await fetch("/api/website-design-settings", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings),
      });
      if (!res.ok) {
        throw new Error("Failed to save");
      }
      toast.success("Website design settings saved");
    } catch (error) {
      console.error("Save website design settings error:", error);
      toast.error("Failed to save website design settings");
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <Card>
        <CardHeader>
          <CardTitle>Colors</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <ColorField
            id="primaryColor"
            label="Primary color"
            value={settings.colors.primary}
            defaultHex="#000000"
            onChange={(primary) =>
              setSettings({
                ...settings,
                colors: { ...settings.colors, primary },
              })
            }
          />
          <ColorField
            id="secondaryColor"
            label="Secondary color"
            value={settings.colors.secondary}
            defaultHex="#f4f4f5"
            onChange={(secondary) =>
              setSettings({
                ...settings,
                colors: { ...settings.colors, secondary },
              })
            }
          />
          <ColorField
            id="accentColor"
            label="Accent color"
            value={settings.colors.accent}
            defaultHex="#6366f1"
            onChange={(accent) =>
              setSettings({
                ...settings,
                colors: { ...settings.colors, accent },
              })
            }
          />
          <ColorField
            id="backgroundColor"
            label="Background color"
            value={settings.colors.background}
            defaultHex="#ffffff"
            onChange={(background) =>
              setSettings({
                ...settings,
                colors: { ...settings.colors, background },
              })
            }
          />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Typography</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-2">
            <Label htmlFor="headingFont">Heading font</Label>
            <FontPicker
              key={`heading-${settings.typography.headingFont || "Roboto"}`}
              inputId="headingFont"
              defaultValue={settings.typography.headingFont || "Roboto"}
              autoLoad
              value={(fontFamily) =>
                setSettings({
                  ...settings,
                  typography: { ...settings.typography, headingFont: fontFamily },
                })
              }
            />
          </div>
          <ColorField
            id="headingColor"
            label="Heading color"
            value={settings.typography.headingColor}
            defaultHex="#000000"
            onChange={(headingColor) =>
              setSettings({
                ...settings,
                typography: { ...settings.typography, headingColor },
              })
            }
          />
          <div className="space-y-2">
            <Label htmlFor="bodyFont">Body font</Label>
            <FontPicker
              key={`body-${settings.typography.bodyFont || "Open Sans"}`}
              inputId="bodyFont"
              defaultValue={settings.typography.bodyFont || "Open Sans"}
              autoLoad
              value={(fontFamily) =>
                setSettings({
                  ...settings,
                  typography: { ...settings.typography, bodyFont: fontFamily },
                })
              }
            />
          </div>
          <ColorField
            id="bodyColor"
            label="Body color"
            value={settings.typography.bodyColor}
            defaultHex="#1a1a1a"
            onChange={(bodyColor) =>
              setSettings({
                ...settings,
                typography: { ...settings.typography, bodyColor },
              })
            }
          />
        </CardContent>
      </Card>

      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={isSaving}>
          {isSaving ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Saving...
            </>
          ) : (
            "Save"
          )}
        </Button>
      </div>
    </div>
  );
}
